/* REIN layer: six small behaviours, no dependencies. Dawn's own scripts (header, cart, product
   form, animations) are untouched. Everything here works without JavaScript too: the carousel
   falls back to a swipeable row, flip cards and spec panels to their front face. */

(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* 1. Header height, for pages whose first section sits under the glass header. */
  const header = document.querySelector('.section-header .header-wrapper');
  if (header) {
    const setHeight = () => document.documentElement.style.setProperty('--rein-header-height', `${header.offsetHeight}px`);
    setHeight();
    if ('ResizeObserver' in window) new ResizeObserver(setHeight).observe(header);
  }

  /* 2. Carousel: scroll-snap track, arrows, pager, optional auto-advance with a pause control.
        Auto-advance never runs for reduced motion, and stops on hover, focus or pause. */
  class ReinCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.pauseButton = this.querySelector('[data-pause]');
      if (!this.track || this.slides.length < 2) return;
      this.index = 0;
      this.delay = Number(this.dataset.autoplay || 0) * 1000;
      this.paused = reduceMotion.matches || !this.delay;

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));
      this.pauseButton?.addEventListener('click', () => this.setPaused(!this.paused, true));
      if (this.pauseButton && (reduceMotion.matches || !this.delay)) this.pauseButton.hidden = true;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) this.sync(this.slides.indexOf(entry.target));
        });
      }, { root: this.track, threshold: [0.6] });
      this.slides.forEach((slide) => observer.observe(slide));

      ['mouseenter', 'focusin'].forEach((e) => this.addEventListener(e, () => this.hold(true)));
      ['mouseleave', 'focusout'].forEach((e) => this.addEventListener(e, () => this.hold(false)));
      this.sync(0);
      this.schedule();
    }

    go(i, user) {
      const n = this.slides.length;
      const next = (i + n) % n;
      this.track.scrollTo({ left: this.slides[next].offsetLeft - this.track.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      this.sync(next);
      if (user) this.setPaused(true);
    }

    sync(i) {
      if (i < 0) return;
      this.index = i;
      this.slides.forEach((slide, k) => {
        slide.setAttribute('aria-hidden', k === i ? 'false' : 'true');
        slide.toggleAttribute('inert', k !== i);
      });
      this.dots.forEach((dot, k) => dot.setAttribute('aria-current', k === i ? 'true' : 'false'));
    }

    hold(on) {
      this.held = on;
      if (!on) this.schedule();
    }

    setPaused(paused, fromButton) {
      this.paused = paused;
      if (this.pauseButton) {
        this.pauseButton.setAttribute('aria-pressed', paused ? 'true' : 'false');
        this.pauseButton.setAttribute('aria-label', paused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel);
      }
      if (!paused && fromButton) this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      if (this.paused || !this.delay) return;
      this.timer = setTimeout(() => {
        if (!this.held && !this.paused && !document.hidden) this.go(this.index + 1, false);
        this.schedule();
      }, this.delay);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
    }
  }

  if (!customElements.get('rein-carousel')) customElements.define('rein-carousel', ReinCarousel);

  /* 3. Flip cards and product spec panels: one button turns the card; the hidden face is inert,
        so keyboard and screen-reader users only ever meet the face on show. */
  const setFace = (card, flipped) => {
    card.classList.toggle('is-flipped', flipped);
    card.querySelectorAll('[data-face="front"]').forEach((face) => face.toggleAttribute('inert', flipped));
    card.querySelectorAll('[data-face="back"]').forEach((face) => face.toggleAttribute('inert', !flipped));
    card.querySelectorAll('[data-flip]').forEach((button) => button.setAttribute('aria-expanded', flipped ? 'true' : 'false'));
  };

  document.querySelectorAll('[data-flip-card]').forEach((card) => setFace(card, false));

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-flip]');
    if (!button) return;
    const card = button.closest('[data-flip-card]');
    if (!card) return;
    const flipped = !card.classList.contains('is-flipped');
    setFace(card, flipped);
    const target = card.querySelector(flipped ? '[data-face="back"] [data-flip]' : '[data-face="front"] [data-flip]');
    if (target) target.focus({ preventScroll: true });
  });

  /* 4. Product gallery on phones: the progress line under the slider follows Dawn's slideChanged
        event. Without this script the line stays hidden and Dawn's counter still works. */
  const setupGalleryProgress = (root) => {
    root.querySelectorAll('media-gallery slider-component:not(.thumbnail-slider)').forEach((slider) => {
      const buttons = slider.querySelector('.slider-buttons');
      const count = slider.querySelectorAll('.product__media-item').length;
      if (!buttons || count < 2 || buttons.hasAttribute('data-rein-progress')) return;
      buttons.style.setProperty('--rein-slides', count);
      buttons.style.setProperty('--rein-slide', 0);
      buttons.setAttribute('data-rein-progress', '');
      slider.addEventListener('slideChanged', (event) => {
        if (slider.totalPages) buttons.style.setProperty('--rein-slides', slider.totalPages);
        buttons.style.setProperty('--rein-slide', event.detail.currentPage - 1);
      });
    });
  };
  setupGalleryProgress(document);

  /* 5. Product gallery arrows on desktop: two glass buttons over the stage press Dawn's own
        thumbnails, previous or next, wrapping at the ends. Dawn keeps the active image, the
        thumbnail state and the screen-reader announcement. CSS shows them from 990px up. */
  const arrowIcon = (path) => `<svg class="rein-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="${path}"/></svg>`;
  const setupGalleryArrows = (root) => {
    root.querySelectorAll('media-gallery').forEach((gallery) => {
      const stage = gallery.querySelector('slider-component:not(.thumbnail-slider)');
      const thumbnails = () => [...gallery.querySelectorAll('.thumbnail-list__item')]
        .filter((item) => getComputedStyle(item).display !== 'none')
        .map((item) => item.querySelector('button.thumbnail'))
        .filter(Boolean);
      if (!stage || stage.querySelector('.rein-gallery-arrows') || thumbnails().length < 2) return;

      const step = (by) => {
        const list = thumbnails();
        const current = list.findIndex((button) => button.hasAttribute('aria-current'));
        list[(Math.max(current, 0) + by + list.length) % list.length]?.click();
      };
      const arrows = document.createElement('div');
      arrows.className = 'rein-gallery-arrows';
      [
        ['prev', -1, stage.querySelector('.slider-button--prev')?.getAttribute('aria-label') || 'Previous image', 'M10 3.5L5.5 8l4.5 4.5'],
        ['next', 1, stage.querySelector('.slider-button--next')?.getAttribute('aria-label') || 'Next image', 'M6 3.5L10.5 8 6 12.5'],
      ].forEach(([name, by, label, path]) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `rein-gallery-arrow rein-gallery-arrow--${name}`;
        button.setAttribute('aria-label', label);
        button.innerHTML = arrowIcon(path);
        button.addEventListener('click', () => step(by));
        arrows.append(button);
      });
      stage.append(arrows);
    });
  };
  setupGalleryArrows(document);

  /* 6. Theme editor: re-run setup when a section is re-rendered. */
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-flip-card]').forEach((card) => setFace(card, false));
    setupGalleryProgress(event.target);
    setupGalleryArrows(event.target);
  });
})();
